'use client';

import Container from '@/components/ui/container';
import { parseHeading, sanitizeHtml } from '@/lib/utils';
import Link from 'next/link';
import { ArrowRight, MessageSquare, Briefcase, Home, Wrench, LucideIcon, icons } from 'lucide-react';

interface ServiceItem {
	title: string;
	description?: string;
	icon?: string;
	link_text?: string;
	link_url?: string;
}

interface ServicesProps {
	data: {
		title: string;
		description?: string;
		service_items?: ServiceItem[];
	};
	itemId?: string;
}

const Services = ({ data, itemId }: ServicesProps) => {
	const { title, description } = data;
	const items = data.service_items || [];

	return (
		<section className="py-16 md:py-24 bg-gray-50" data-directus-id={itemId}>
			<Container>
				{/* Header Section */}
				<div className="max-w-3xl mx-auto text-center mb-16">
					<h2
						className="text-3xl md:text-4xl font-heading text-blue-dark mb-6"
						data-vercel-edit-target={`block_services:${itemId}:title`}
					>
						{parseHeading(title)}
					</h2>
					{description && (
						<div
							className="text-gray-dark text-lg leading-relaxed prose max-w-none"
							data-vercel-edit-target={`block_services:${itemId}:description`}
							dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
						/>
					)}
				</div>

				{/* Cards Section */}
				{items.length > 0 && (
					<div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
						{items.map((item, index) => {
							/* eslint-disable import/namespace */
							// Dynamically resolve icon, fallback to Home if not found or invalid
							const IconComponent =
								item.icon && icons[item.icon as keyof typeof icons]
									? (icons[item.icon as keyof typeof icons] as LucideIcon)
									: Home;
							/* eslint-enable import/namespace */

							return (
								<div
									key={index}
									className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 group flex flex-col items-start hover:-translate-y-1"
								>
									<div className="bg-seaweed/10 p-3 rounded-xl mb-6 group-hover:bg-seaweed/20 transition-colors">
										<IconComponent className="size-8 text-seaweed" />
									</div>
									<h3 className="text-2xl font-bold font-heading text-blue-dark mb-3">{item.title}</h3>
									{item.description && <p className="text-gray-muted mb-6 flex-grow text-lg">{item.description}</p>}
									{item.link_url && (
										<Link
											href={item.link_url}
											className="mt-auto flex items-center text-orange font-bold text-lg group-hover:gap-2 transition-all"
										>
											{item.link_text || 'Bekijk meer'}{' '}
											<ArrowRight className="ml-2 size-5 transition-transform group-hover:translate-x-1" />
										</Link>
									)}
								</div>
							);
						})}
					</div>
				)}
			</Container>
		</section>
	);
};

export default Services;
